import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  useScrollTrigger,
  Slide,
  Avatar,
  Divider,
  ListItemIcon,
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import marketingTheme from '../themes/marketingTheme';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

import { isFeatureEnabled } from '../utils/FeatureFlags';
import { useAuth } from '../utils/AuthContext';
import SiteSelector from './SiteSelector';

function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  
  const [productMenuOpen, setProductMenuOpen] = useState(false);
  const [industriesMenuOpen, setIndustriesMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [productMenuPosition, setProductMenuPosition] = useState({ top: 0, left: 0 });
  const [industriesMenuPosition, setIndustriesMenuPosition] = useState({ top: 0, left: 0 });
  const [userMenuPosition, setUserMenuPosition] = useState({ top: 0, left: 0 });

  const handleGetStartedClick = () => {
    if (isFeatureEnabled('SAAS_ONBOARDING')) {
      navigate('/demo?context=productivity&positioning=value_teaser');
    } else {
      // Default behavior: go to contact page (which has Calendly)
      navigate('/contact');
    }
  };

  const isProductPage = location.pathname.startsWith('/product');
  const isSolutionPage = location.pathname.startsWith('/solution');

  const handleProductMenuOpen = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setProductMenuPosition({
      top: rect.bottom + 8, // 8px offset below button
      left: rect.left
    });
    setProductMenuOpen(true);
  };

  const handleProductMenuClose = () => {
    setProductMenuOpen(false);
  };

  const handleIndustriesMenuOpen = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setIndustriesMenuPosition({
      top: rect.bottom + 8, // 8px offset below button
      left: rect.left
    });
    setIndustriesMenuOpen(true);
  };

  const handleIndustriesMenuClose = () => {
    setIndustriesMenuOpen(false);
  };

  const handleUserMenuClose = () => {
    setUserMenuOpen(false);
  };

  const handleUserMenuItemClick = (route) => {
    navigate(route);
    handleUserMenuClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    handleUserMenuClose();
  };

  const NavButton = ({ children, active, ...props }) => (
    <Button
      {...props}
      sx={{
        color: active ? marketingTheme.palette.primary.main : 'rgba(255, 255, 255, 0.7)',
        fontWeight: 600,
        fontSize: '1.1rem',
        textTransform: 'none',
        '&:hover': {
          color: marketingTheme.palette.primary.main,
          backgroundColor: 'transparent',
        },
      }}
    >
      {children}
    </Button>
  );

  return (
    <HideOnScroll>
      <AppBar position="fixed" elevation={0} sx={{ zIndex: 1300 }}>
        <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 4 } }}>
          {/* Logo */}
          <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              // src="/images/motionlogic_logo_w_text.png"
              src="/images/motionlogic_logo_transparent.png"
              alt="MotionLogic"
              style={{ height: '70px' }}
            />
          </Box>

          {/* Navigation */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NavButton
              component={Link}
              to="/"
              active={location.pathname === '/'}
            >
              Home
            </NavButton>

            {/* MotionLogic® Dropdown */}
            <NavButton
              active={isProductPage}
              onClick={handleProductMenuOpen}
              endIcon={<ExpandMoreIcon />}
            >
              {/* MotionLogic® */}
              Product
            </NavButton>
            <Menu
              open={productMenuOpen}
              onClose={handleProductMenuClose}
              PaperProps={{
                sx: {
                  position: 'fixed',
                  top: `${productMenuPosition.top}px !important`,
                  left: `${productMenuPosition.left}px !important`,
                  backgroundColor: '#2d2d33',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  minWidth: 400,
                  transform: 'none !important',
                },
              }}
              BackdropProps={{
                invisible: true
              }}
            >
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ p: 2, minWidth: 200 }}>
                  <MenuItem
                    component={Link}
                    to="/product/overview"
                    onClick={handleProductMenuClose}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
                  >
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>Platform Overview</Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                        Spatial Computing Platform
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/product/worker-safety"
                    onClick={handleProductMenuClose}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
                  >
                    Worker Safety
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/product/ergonomic-analytics"
                    onClick={handleProductMenuClose}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
                  >
                    Ergonomics
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/product/time-and-motion"
                    onClick={handleProductMenuClose}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
                  >
                    Time-and-Motion
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/product/lean-waste-analytics"
                    onClick={handleProductMenuClose}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
                  >
                    Lean Wastes
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/product/spaghetti-analytics"
                    onClick={handleProductMenuClose}
                    sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
                  >
                    Spaghetti Analysis
                  </MenuItem>
                </Box>
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minWidth: 200,
                  }}
                >
                  <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold', mb: 1 }}>
                    Vision AI for Industrial
                    <br />
                    Productivity & Safety
                  </Typography>
                </Box>
              </Box>
            </Menu>

            {/* Industries Dropdown */}
            <NavButton
              active={isSolutionPage}
              onClick={handleIndustriesMenuOpen}
              endIcon={<ExpandMoreIcon />}
            >
              Industries
            </NavButton>
            <Menu
              open={industriesMenuOpen}
              onClose={handleIndustriesMenuClose}
              PaperProps={{
                sx: {
                  position: 'fixed',
                  top: `${industriesMenuPosition.top}px !important`,
                  left: `${industriesMenuPosition.left}px !important`,
                  backgroundColor: '#2d2d33',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transform: 'none !important',
                },
              }}
              BackdropProps={{
                invisible: true
              }}
            >
              <MenuItem
                component={Link}
                to="/solutions/manufacturing"
                onClick={handleIndustriesMenuClose}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
              >
                Manufacturing
              </MenuItem>
              <MenuItem
                component={Link}
                to="/solutions/logistics"
                onClick={handleIndustriesMenuClose}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.5)' } }}
              >
                Logistics
              </MenuItem>
            </Menu>
            
            {/* User Menu */}
            <Menu
              open={userMenuOpen}
              onClose={handleUserMenuClose}
              PaperProps={{
                sx: {
                  position: 'fixed',
                  top: `${userMenuPosition.top}px !important`,
                  left: `${userMenuPosition.left}px !important`,
                  backgroundColor: '#2d2d33',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  minWidth: 200,
                  transform: 'none !important',
                },
              }}
              BackdropProps={{
                invisible: true
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>
                  {user?.name || user?.email || 'User'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  {user?.company || user?.email}
                </Typography>
              </Box>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <MenuItem
                onClick={() => handleUserMenuItemClick('/dashboard')}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <ListItemIcon>
                  <DashboardIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                Dashboard
              </MenuItem>
              <MenuItem
                onClick={() => handleUserMenuItemClick('/my-account')}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <ListItemIcon>
                  <AccountCircleIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                My Account
              </MenuItem>
              <MenuItem
                onClick={() => handleUserMenuItemClick('/account-settings')}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <ListItemIcon>
                  <SettingsIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                Settings
              </MenuItem>
              <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />
              <MenuItem
                onClick={handleLogout}
                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
              >
                <ListItemIcon>
                  <ExitToAppIcon sx={{ color: 'white' }} />
                </ListItemIcon>
                Sign Out
              </MenuItem>
            </Menu>

            <NavButton
              component={Link}
              to="/resource-library"
              active={location.pathname === '/resource-library'}
            >
              Insights
            </NavButton>

            <NavButton
              component={Link}
              to="/contact"
              active={location.pathname === '/contact'}
            >
              Contact
            </NavButton>

            {/* Authentication-aware buttons */}
            {isFeatureEnabled('SAAS_ONBOARDING') && isAuthenticated ? (
              // User is logged in - show site selector and user avatar
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SiteSelector variant="header" />
                <Button
                  component={Link}
                  to="/dashboard"
                  variant="outlined"
                  size="small"
                  sx={{
                    borderColor: marketingTheme.palette.primary.main,
                    color: marketingTheme.palette.primary.main,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: marketingTheme.palette.primary.dark,
                      backgroundColor: 'rgba(57, 102, 237, 0.1)',
                    },
                  }}
                >
                  Dashboard
                </Button>
                <Button
                  onClick={(e) => setUserMenuPosition({ 
                    top: e.currentTarget.getBoundingClientRect().bottom + 8,
                    left: e.currentTarget.getBoundingClientRect().left 
                  }) || setUserMenuOpen(true)}
                  sx={{
                    minWidth: 'auto',
                    padding: '4px',
                    borderRadius: '50%',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: marketingTheme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#000',
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  >
                    {user?.name ? user.name.charAt(0).toUpperCase() : user?.email?.charAt(0).toUpperCase() || 'U'}
                  </Box>
                </Button>
              </Box>
            ) : (
              // User is not logged in - show Get Started button
              <Button
                onClick={handleGetStartedClick}
                variant="contained"
                size="small"
                // Play icon
                startIcon={<PlayArrowIcon />}
                sx={{
                  backgroundColor: marketingTheme.palette.primary.main,
                  color: '#000',
                  fontWeight: 600,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: marketingTheme.palette.primary.dark,
                  },
                }}
              >
                {/* Get Started */}
                Experience MotionLogic
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>
    </HideOnScroll>
  );
};

export default Header;
